'use client';

import { Button } from '@tuic/web-compositions/core/Button';
import { Dialog, DialogContent } from '@tuic/web-compositions/shared/Dialog';
import {
  getResponsiveValue,
  ResponsiveValue,
} from '@tuic/web-compositions/utils/responsive-property';
import {
  ResponsiveVideo,
  ResponsiveVideoProps,
} from '@tuic/web-compositions/shared/Video';
import { useScreenName } from '@tuic/web-compositions/hooks/use-screen-name';
import { FC, ReactNode, useMemo, useState } from 'react';
import { useClientTranslation } from '@lib/i18n/client';
import { Locale } from '@tuic/i18n';
import { SmartLink } from './smart-link';

export type ExplanationVideoOptions = {
  locale: Locale;

  title: ResponsiveValue<string>;
  description: ResponsiveValue<ReactNode>;
  video?: ResponsiveVideoProps;

  link?: string;
  onClick?: () => void;
};

export type WithExplanationVideoDialogProps = {
  children: (open: () => void) => ReactNode;
} & ExplanationVideoOptions;

export const WithExplanationVideoDialog: FC<
  WithExplanationVideoDialogProps
> = ({ locale, title, description, video, link, onClick, children }) => {
  const [t] = useClientTranslation(locale);
  const [open, setOpen] = useState(false);

  const screen = useScreenName();

  const titleContent = useMemo(
    () => getResponsiveValue(title, '', screen),
    [title, screen],
  );
  const descriptionContent = useMemo(
    () => getResponsiveValue(description, null, screen),
    [description, screen],
  );

  const button = useMemo(
    () =>
      link ? (
        <Button asChild>
          <SmartLink href={link}>{t('Mehr Informationen')}</SmartLink>
        </Button>
      ) : onClick ? (
        <Button onClick={onClick}>{t('Mehr Informationen')}</Button>
      ) : null,
    [link, onClick, t],
  );

  return (
    <>
      {children(() => setOpen(true))}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={titleContent} disableSpacing>
          <div className="flex flex-col gap-8 p-6 xl:p-10">
            <ResponsiveVideo {...video} />
            <div className="text-high-emphasis flex flex-col gap-2">
              <h2 className="text-800">{titleContent}</h2>
              <div>{descriptionContent}</div>
            </div>
            <div className="flex flex-col md:flex-row justify-end">
              {button}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ExplanationVideoAsChildWrapper: FC<ExplanationVideoOptions> = ({
  locale,
  title,
  description,
  video,
  link,
  onClick,

  /**
   * pass all other props when parent uses "asChild" to
   * the underlying button and not the video dialog
   */
  ...props
}) => {
  return (
    <WithExplanationVideoDialog
      locale={locale}
      title={title}
      description={description}
      video={video}
      link={link}
      onClick={onClick}
    >
      {open => <button {...props} onClick={open} />}
    </WithExplanationVideoDialog>
  );
};
